import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, ExternalLink } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { GuardOrb3D, LEVEL_COLOR } from "./GuardOrb3D";
import { GuardConsole } from "./GuardConsole";
import { Findings, Events } from "./Findings";
import { useGuard } from "./GuardContext";

const LABEL = { secure: "SECURE", watch: "WATCHING", threat: "THREAT" };

export const GuardOverlay = () => {
  const { st, reload } = useGuard();
  const { isOwner } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("console");
  const idle = useRef(0);
  const level = st?.level || "secure";
  const color = LEVEL_COLOR[level];
  return (
    <>
      <button onClick={() => setOpen(!open)} className="fixed bottom-4 right-48 z-40 w-16 h-16 rounded-full guard-orb-float" title="AEGIS Guard" data-testid="guard-orb-btn"
        style={{ boxShadow: `0 0 40px ${color}55, inset 0 0 20px ${color}33` }}>
        <GuardOrb3D level={level} mode={open ? "listening" : "idle"} levelRef={idle} className="w-16 h-16" testid="guard-orb-mini" />
        <span className="absolute -top-1 -left-1 font-mono text-[8px] tracking-widest px-1.5 py-0.5 bg-black/85 border" style={{ color, borderColor: `${color}88` }} data-testid="guard-level-badge">
          {LABEL[level]}{st?.high ? ` · ${st.high}` : ""}</span>
      </button>
      {open && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(440px,calc(100vw-2rem))] max-h-[calc(100vh-2rem)] overflow-y-auto border bg-[#05070A]/90 backdrop-blur-2xl p-4 guard-panel-in"
          style={{ borderColor: `${color}55`, boxShadow: `0 0 60px ${color}22` }} data-testid="guard-panel">
          <div className="flex items-center justify-between mb-3">
            <div><p className="font-mono text-xs tracking-[0.3em]" style={{ color }}>AEGIS GUARD · {LABEL[level]}</p>
              <p className="text-[11px] text-white/45">{st ? `${st.findings.length} finding(s) · ${st.events.length} event(s) 24h${st.admin ? ` · ${st.open_errors} open error(s)` : ""}` : "connecting…"}</p></div>
            <div className="flex items-center gap-3">
              <Link to="/app/security" onClick={() => setOpen(false)} className="text-white/50 hover:text-white" title="Open Security page" data-testid="guard-open-page"><ExternalLink className="w-4 h-4" /></Link>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white" data-testid="guard-close"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex gap-1 mb-3 border-b border-white/10">
            {[["console", "Ask Guard"], ["threats", "Threats"], ["events", "Live events"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 font-mono text-[10px] tracking-widest uppercase border-b-2 -mb-px ${tab === k ? "text-white" : "border-transparent text-white/45 hover:text-white"}`}
                style={tab === k ? { borderColor: color } : undefined} data-testid={`guard-tab-${k}`}>{l}</button>
            ))}
          </div>
          {tab === "console" && <GuardConsole level={level} session="overlay" testid="guard-overlay-console" />}
          {tab === "threats" && st && <div className="max-h-[60vh] overflow-y-auto"><Findings items={st.findings} reload={reload} canAct={isOwner} admin={st.admin} max={20} testid="guard-overlay-findings" /></div>}
          {tab === "events" && st && <div className="max-h-[60vh] overflow-y-auto"><Events items={st.events} testid="guard-overlay-events" /></div>}
        </div>
      )}
    </>
  );
};
